<script lang="ts" setup>
const emit = defineEmits(["close", "skip"]);
const props = defineProps({
  rescue: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const userStore = useUserStore();
const { $serverApi } = useNuxtApp();

const methods = ref([
  {
    name: "main.modalUserWalletCreate.socialTitle",
    id: "google" as Extract<UserKeyMethod, "google">,
    description: "main.modalUserWalletCreate.socialDescription",
    icon: "google",
    enabled: true,
  },
  {
    name: "main.modalUserWalletCreate.socialTitle",
    id: "apple" as Extract<UserKeyMethod, "apple">,
    description: "main.modalUserWalletCreate.socialDescription",
    icon: "apple",
    enabled: true,
  },
  {
    name: "main.modalUserWalletCreate.socialTitle",
    id: "facebook" as Extract<UserKeyMethod, "facebook">,
    description: "main.modalUserWalletCreate.socialDescription",
    icon: "facebook",
    enabled: true,
  },
]);

const alreadyLinkedMethod = computed(() => {
  if (userStore.identityProviders?.google) return "google";
  if (userStore.identityProviders?.apple) return "apple";
  if (userStore.identityProviders?.facebook) return "facebook";
  return null;
});

const processingLogin = ref<Extract<
  UserKeyMethod,
  "google" | "apple" | "facebook"
> | null>(null);

const getLinkActionUrl = () => {
  if (props.rescue) {
    if (alreadyLinkedMethod.value) return "rescue/existing-link";
    else return "rescue/link";
  } else if (alreadyLinkedMethod.value) return "new-wallet-existing-link";
  else return "link";
};

const handleSelectedMethod = async (
  methodId: Extract<UserKeyMethod, "google" | "apple" | "facebook">
) => {
  processingLogin.value = methodId;
  if (
    methodId === "google" ||
    methodId === "apple" ||
    methodId === "facebook"
  ) {
    const linkactionUrl = getLinkActionUrl();
    const requestBody = {
      methodId,
      path: getCurrentFullPath(route.path),
    };
    const headers = {
      Authorization: `Bearer ${useUserStore().authToken}`,
    };
    let response: any;
    try {
      response = await $serverApi(`/oauth/${linkactionUrl}/`, {
        method: "POST",
        headers,
        body: requestBody,
      });
    } catch {
      response = await $fetch(`/oauth/${linkactionUrl}/`, {
        method: "POST",
        headers,
        body: requestBody,
      });
    }
    if (response.statusCode !== 200)
      throw new Error("Failed to get oauth link");
    else navigateTo(response.payload, { external: true });
  }
};
</script>

<template>
  <div class="mt-6 mb-2 flex flex-col items-start gap-4">
    <p v-if="!alreadyLinkedMethod" class="text-neutral-300">
      {{ $t("main.modalUserWalletCreate.securitySocial") }}
    </p>
    <p v-else class="text-neutral-300">
      {{ $t("main.modalUserWalletCreate.securitySocialExisting") }}
    </p>
    <div class="flex flex-col items-start gap-2 w-full">
      <p class="text-neutral-300 text-xs font-bold">
        {{ $t("main.modalUserWalletCreate.socialAccount") }}
        <span class="text-success-300">{{
          $t("main.modalUserWalletCreate.recommended")
        }}</span>
      </p>
      <div
        v-if="!alreadyLinkedMethod"
        v-for="method in methods"
        :key="method.id"
        class="group flex flex-col gap-4 bg-neutral-500 rounded-xl p-4 w-full text-neutral-300 cursor-pointer hover:text-primary-500">
        <div :key="method.id" @click="handleSelectedMethod(method.id)">
          <div class="flex items-center justify-between gap-4">
            <BaseIcon :name="method.icon" class="w-7 h-7" />
            <div class="flex-1 flex flex-col">
              <p
                class="font-bold text-neutral-200 group-hover:text-primary-500">
                {{ $t(method.name, { social: $t("main." + method.id) }) }}
              </p>
              <p class="text-sm">
                {{
                  $t(method.description, { social: $t("main." + method.id) })
                }}
              </p>
            </div>
            <NuxtIcon
              v-if="processingLogin !== method.id"
              name="mdi:external-link"
              class="w-6 h-6" />
            <LoaderSpinner v-else />
          </div>
        </div>
      </div>
      <div v-else class="w-full">
        <template v-for="method in methods" :key="method.id">
          <div
            @click="handleSelectedMethod(method.id)"
            v-if="method.id === alreadyLinkedMethod"
            class="group flex flex-col gap-4 bg-neutral-500 rounded-xl p-4 w-full text-neutral-300 cursor-pointer hover:text-primary-500">
            <div class="flex items-center justify-between gap-4">
              <BaseIcon :name="method.icon" class="w-7 h-7" />
              <div class="flex-1 flex flex-col">
                <p
                  class="font-bold text-neutral-200 group-hover:text-primary-500">
                  {{ $t(method.name, { social: $t("main." + method.id) }) }}
                </p>
                <p class="text-sm">
                  {{
                    $t(method.description, { social: $t("main." + method.id) })
                  }}
                </p>
              </div>
              <div class="flex items-center gap-4">
                <NuxtIcon
                  v-if="processingLogin !== method.id"
                  name="mdi:external-link"
                  class="w-6 h-6" />
                <LoaderSpinner v-else />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <button
      @click="emit('skip')"
      class="mt-4 text-sm text-primary-500 hover:text-error-300 cursor-pointer underline">
      {{ $t("main.modalUserWalletCreate.skipSocial") }}
    </button>
  </div>
</template>
